import { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  SettingsTabs,
  ProfileSettings,
  FinancialPlanningSettings,
  SecuritySettings,
  PrivacyDataSettings,
  NotificationSettings,
  BillingSettings,
  ConnectionsSettings,
  DangerZoneSettings,
  HouseholdSettings,
  type SettingsTab,
} from '@/components/settings';

const VALID_TABS: SettingsTab[] = ['profile', 'household', 'security', 'privacy', 'connections', 'notifications', 'billing'];

/**
 * Parse tab from URL path parameter or hash (for backward compatibility)
 */
function getValidTab(tab: string | undefined, hash: string): SettingsTab {
  // First try the path parameter
  if (tab && VALID_TABS.includes(tab as SettingsTab)) {
    return tab as SettingsTab;
  }

  // Fallback to hash for backward compatibility with old bookmarks
  const hashTab = hash.replace('#', '') as SettingsTab;
  if (VALID_TABS.includes(hashTab)) {
    return hashTab;
  }

  return 'profile';
}

/**
 * Settings Page - User account settings and preferences
 *
 * Uses path-based routing for tabs:
 * - /settings (defaults to profile)
 * - /settings/profile
 * - /settings/household
 * - /settings/security
 * - /settings/connections
 * - /settings/notifications
 * - /settings/billing
 *
 * Also supports legacy hash-based URLs for backward compatibility:
 * - /settings#household → redirects to /settings/household
 */
export default function SettingsPage() {
  const { tab } = useParams<{ tab?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = getValidTab(tab, location.hash);

  // Redirect hash-based URLs to path-based for SEO and consistency
  useEffect(() => {
    if (location.hash && !tab) {
      const hashTab = location.hash.replace('#', '');
      if (VALID_TABS.includes(hashTab as SettingsTab)) {
        navigate(`/settings/${hashTab}`, { replace: true });
      }
    }
  }, [location.hash, tab, navigate]);

  // Handle tab change from sidebar/dropdown
  const handleTabChange = (newTab: SettingsTab) => {
    navigate(`/settings/${newTab}`, { replace: true });
  };

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <ProfileSettings />
            <FinancialPlanningSettings />
            <DangerZoneSettings />
          </div>
        );
      case 'household':
        return <HouseholdSettings />;
      case 'security':
        return <SecuritySettings />;
      case 'privacy':
        return <PrivacyDataSettings />;
      case 'connections':
        return <ConnectionsSettings />;
      case 'notifications':
        return <NotificationSettings />;
      case 'billing':
        return <BillingSettings />;
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-gray-600">
          Manage your account settings and preferences.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar with tabs - hidden on mobile, shown in dropdown instead */}
        <aside className="lg:w-64 flex-shrink-0">
          {/* Mobile tab selector */}
          <div className="lg:hidden mb-4">
            <label htmlFor="settings-tab-mobile" className="sr-only">
              Select a settings section
            </label>
            <select
              id="settings-tab-mobile"
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value as SettingsTab)}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="profile">Profile</option>
              <option value="household">Household</option>
              <option value="security">Security</option>
              <option value="privacy">Privacy & Data</option>
              <option value="connections">Connections</option>
              <option value="notifications">Notifications</option>
              <option value="billing">Billing</option>
            </select>
          </div>

          {/* Desktop tabs */}
          <div className="hidden lg:block sticky top-4">
            <SettingsTabs activeTab={activeTab} onTabChange={handleTabChange} />
          </div>
        </aside>

        {/* Main content area */}
        <main className="flex-1 min-w-0">
          {renderTabContent()}
        </main>
      </div>
    </div>
  );
}
